import { supabase } from "@/integrations/supabase/client";

/**
 * Helper functions for working with the new workflow system
 */

export interface Workflow {
  id: string;
  workflow_key: string;
  title: string;
  description: string;
  category: string;
  estimated_time: string;
  difficulty: string;
  prerequisites: any;
  phases: any;
  icon_name: string;
  tier_restrictions: any;
  is_active: boolean;
  success_criteria: string[];
  guidance_notes?: string;
}

export interface ActiveWorkflow {
  id: string;
  workflow_id: string;
  task_id: string;
  workflow: Workflow;
  current_phase: number;
  started_at: string;
  completed_at?: string;
  completion_percentage: number;
}

/**
 * Get all available workflows for the current user
 */
export async function getAvailableWorkflows(): Promise<Workflow[]> {
  const { data, error } = await supabase
    .from('workflows')
    .select('*')
    .eq('is_active', true)
    .order('sort_order');
  
  if (error) {
    console.error('Error fetching workflows:', error);
    return [];
  }
  
  return (data || []) as Workflow[];
}

/**
 * Get workflows by category
 */
export async function getWorkflowsByCategory(category: string): Promise<Workflow[]> {
  const { data, error } = await supabase
    .from('workflows')
    .select('*')
    .eq('category', category)
    .eq('is_active', true)
    .order('sort_order');
  
  if (error) {
    console.error('Error fetching workflows by category:', error);
    return [];
  }
  
  return (data || []) as Workflow[];
}

/**
 * Get user's active workflows
 */
export async function getUserActiveWorkflows(userId: string): Promise<ActiveWorkflow[]> {
  const { data, error } = await supabase
    .rpc('get_user_active_workflows' as any, { p_user_id: userId }) as { data: any; error: any };
  
  if (error) {
    console.error('Error fetching active workflows:', error);
    return [];
  }
  
  return (data || []) as ActiveWorkflow[];
}

/**
 * Activate a workflow for a task
 */
export async function activateWorkflow(
  workflowId: string,
  taskId: string,
  workflowData: any = {}
): Promise<{ success: boolean; error?: string; activationId?: string }> {
  try {
    // Check if workflow already activated for this task
    const { data: existing } = await supabase
      .from('task_workflows')
      .select('id')
      .eq('task_id', taskId)
      .maybeSingle();
    
    if (existing) {
      return { success: false, error: 'Workflow already activated for this task' };
    }
    
    // Get task details including una_id
    const { data: task } = await supabase
      .from('custom_tasks')
      .select('una_id, user_id')
      .eq('id', taskId)
      .single();
    
    if (!task) {
      return { success: false, error: 'Task not found' };
    }
    
    // Get workflow details to extract phases
    const { data: workflow, error: workflowError } = await supabase
      .from('workflows')
      .select('phases, title')
      .eq('id', workflowId)
      .single();
    
    if (workflowError || !workflow) {
      return { success: false, error: 'Workflow not found' };
    }
    
    // Create workflow activation with una_id
    const { data, error } = await supabase
      .from('task_workflows')
      .insert({
        task_id: taskId,
        workflow_id: workflowId,
        una_id: task.una_id, // CRITICAL: Include una_id
        workflow_data: workflowData,
        current_phase: 1,
      })
      .select()
      .single();
    
    if (error) {
      return { success: false, error: error.message };
    }
    
    // CRITICAL: Create task_phases entries from workflow phases
    const phases = Array.isArray(workflow.phases) ? workflow.phases : [];
    if (phases.length > 0) {
      const taskPhases = phases.map((phase: any) => ({
        task_id: taskId,
        una_id: task.una_id,
        phase_number: phase.phase_number,
        phase_name: phase.phase_title || phase.phase_name,
        phase_description: phase.phase_description || phase.description,
        order_number: phase.phase_number,
        status: phase.phase_number === 1 ? 'in_progress' : 'pending',
        requirements: phase.tasks ? phase.tasks.map((t: any, idx: number) => ({
          id: `req-${idx}`,
          description: typeof t === 'string' ? t : t.task_title,
          completed: false,
        })) : [],
        document_requirements: phase.documents || [],
        estimated_duration_days: phase.estimated_hours ? Math.ceil(phase.estimated_hours / 8) : null,
      }));
      
      const { error: phasesError } = await supabase
        .from('task_phases')
        .insert(taskPhases);
      
      if (phasesError) {
        console.error('Error creating task phases:', phasesError);
        // Don't fail the whole operation, just log it
      }
    }
    
    // Update task to link to workflow
    await supabase
      .from('custom_tasks')
      .update({
        workflow_id: workflowId,
        is_from_workflow: true,
      })
      .eq('id', taskId);
    
    // Create workflow completion tracking record
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase
        .from('workflow_completion_tracking')
        .insert({
          workflow_id: workflowId,
          user_id: user.id,
          una_id: task.una_id,
          started_at: new Date().toISOString(),
          total_phases: phases.length,
        });
    }
    
    return { success: true, activationId: data.id };
  } catch (error: any) {
    console.error('Error activating workflow:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update workflow phase
 */
export async function updateWorkflowPhase(
  taskId: string,
  phaseNumber: number
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('task_workflows')
      .update({ current_phase: phaseNumber })
      .eq('task_id', taskId);
    
    if (error) {
      return { success: false, error: error.message };
    }
    
    return { success: true };
  } catch (error: any) {
    console.error('Error updating workflow phase:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Complete a workflow
 */
export async function completeWorkflow(
  taskId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('task_workflows')
      .update({ completed_at: new Date().toISOString() })
      .eq('task_id', taskId);
    
    if (error) {
      return { success: false, error: error.message };
    }
    
    return { success: true };
  } catch (error: any) {
    console.error('Error completing workflow:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Check if user has access to workflow based on tier
 */
export async function canAccessWorkflow(workflow: Workflow): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  
  const { data: profile } = await supabase
    .from('profiles')
    .select('subscription_tier')
    .eq('id', user.id)
    .maybeSingle();
  
  // If we can't find a profile, default to most permissive tier (Premium)
  const userTier = (profile?.subscription_tier as string) || 'Premium';

  return canAccessWorkflowSync(workflow, userTier);
}

/**
 * Synchronous version of canAccessWorkflow that takes userTier as parameter
 * Use this when you already have the user's tier to avoid redundant DB queries
 */
export function canAccessWorkflowSync(workflow: Workflow, userTier: string): boolean {
  // Map legacy/new tier names to workflow tier labels
  // App tiers: DIY | Guided | Premium
  // Workflow tiers: Foundation | Active Path | Elite
  const tierMapping: Record<string, string> = {
    DIY: 'Foundation',
    Guided: 'Active Path',
    Premium: 'Elite',
  };

  // If no tier restrictions, everyone can access
  if (!workflow.tier_restrictions || workflow.tier_restrictions.length === 0) {
    return true;
  }

  const mappedTier = tierMapping[userTier] || userTier;
  return Array.isArray(workflow.tier_restrictions)
    ? workflow.tier_restrictions.includes(mappedTier)
      || workflow.tier_restrictions.includes(userTier)
    : true;
}

/**
 * Get workflow categories
 */
export function getWorkflowCategories(): { value: string; label: string }[] {
  return [
    { value: 'member_operations', label: 'Member Operations' },
    { value: 'asset_management', label: 'Asset Management' },
    { value: 'governance', label: 'Governance & Compliance' },
    { value: 'financial_operations', label: 'Financial Operations' },
    { value: 'estate_planning', label: 'Estate Planning' },
  ];
}

/**
 * Track content usage
 */
export async function trackContentUsage(
  contentType: string,
  contentId: string,
  actionType: string,
  metadata: any = {}
): Promise<void> {
  try {
    // Validate contentId as UUID - skip tracking if invalid
    const { isValidUUID } = await import("@/lib/utils");
    if (!isValidUUID(contentId)) {
      console.warn('[Analytics] Skipping tracking - invalid UUID:', contentId);
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();

    await supabase
      .from('content_usage_analytics')
      .insert({
        content_type: contentType,
        content_id: contentId,
        user_id: user?.id,
        action_type: actionType,
        metadata,
      });
  } catch (error) {
    console.error('Error tracking content usage:', error);
  }
}
